import { SUPPORTED_EXCHANGES, type TokenPairSymbol } from "@cede/types";
import { CEDE_CEXS, isDexId } from "../cexs/constants";
import { Logger } from "../logger";

const KENOBI_WATCHABLE_EXCHANGE_IDS = new Set<string>(Object.values(SUPPORTED_EXCHANGES));

/**
 * Whether Kenobi can run an orderbook spread watcher on this venue. DEXes have their own
 * onchain watcher, `paper` is synthetic with no real book, and anything outside
 * `SUPPORTED_EXCHANGES` has no adapter at all.
 *
 * That last case is not hypothetical: the TTE CMS picker is still built from the deprecated
 * `EXCHANGES` list, so an admin can select `bitfinex`, `bitstamp` or `coinbaseexchange`. A
 * campaign on one of those is a gap no backfill can ever close, so it must not read as watchable
 * or the repair loop retries it forever. The caller reports it once instead.
 *
 * Shared so campaign registration and the backfill that repairs it cannot disagree about which
 * venues are in scope: a venue watchable to one and not the other would either leave a campaign
 * silently uncovered or thrash on a gap that can never be filled.
 */
export function isKenobiOrderbookWatchable(exchangeId: string): boolean {
  if (isDexId(exchangeId) || exchangeId === CEDE_CEXS.PAPER) return false;
  return KENOBI_WATCHABLE_EXCHANGE_IDS.has(exchangeId);
}

function getQuoteCurrency(exchangeId: string): string {
  if (isDexId(exchangeId)) {
    return "USDC";
  }
  switch (exchangeId) {
    case SUPPORTED_EXCHANGES.CRYPTOCOM:
      return "USD";
    default:
      return "USDT";
  }
}

export function getWatcherPairSymbol(
  tokenSymbol: string,
  exchangeId: string,
  pairSymbols?: TokenPairSymbol[],
): TokenPairSymbol {
  if (pairSymbols && pairSymbols.length > 0 && pairSymbols[0]) {
    return pairSymbols[0];
  }
  return `${tokenSymbol}/${getQuoteCurrency(exchangeId)}`;
}

// Chain name (from DEX exchange ID) → chain ID (for Kenobi onchain watcher)
const BLOCKCHAIN_TO_CHAIN_ID: Record<string, string> = {
  ethereum: "1",
  bsc: "56",
  base: "8453",
  arbitrum: "42161",
  solana: "solana",
};

// Well-known USDC addresses per chain ID
const USDC_ADDRESSES: Record<string, string> = {
  "1": "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", // Ethereum
  "56": "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d", // BNB Chain — 18 decimals, unlike the 6-decimal USDC on every other chain here
  "8453": "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // Base
  "42161": "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", // Arbitrum
  "solana": "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", // Solana
};

/**
 * Extract chain name and protocol from a DEX exchange ID (e.g., "aerodrome-cl|base" → { protocol: "aerodrome-cl", chain: "base" })
 */
export function parseDexExchangeId(exchangeId: string): { protocol: string; chain: string } | null {
  const parts = exchangeId.split("|");
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  return { protocol: parts[0], chain: parts[1] };
}

/**
 * Get chain ID from blockchain name (e.g., "base" → "8453")
 */
export function getChainIdFromBlockchain(blockchain: string): string | null {
  return BLOCKCHAIN_TO_CHAIN_ID[blockchain] ?? null;
}

/**
 * Get default USDC address for a given chain ID
 */
export function getUsdcAddress(chainId: string): string | null {
  return USDC_ADDRESSES[chainId] ?? null;
}

export interface KenobiConfig {
  kenobiApiUrl: string;
  kenobiApiToken?: string;
  nodeEnv: "development" | "production" | "staging" | "test";
}

// use it in kenobi once merged
export interface WashTradeDetectionRequest {
  id: string;
  timestamp: number;
  pairSymbol: string;
  orderType: string;
  side: "buy" | "sell";
  price: number;
  amount: number;
  takerOrMaker: "taker" | "maker";
  exchange: string;
  userId: string;
}

// use it in kenobi once merged
export interface WashTradeDetectionResponse {
  detected: boolean;
  snapshotTime?: number;
  orderTime?: number;
  side?: string;
  sizeAtPrice?: number;
  moAmount?: number;
  moPrice?: number;
  moSide?: string;
  confidence?: number;
  lagMs?: number;
  tradeId?: string;
  userId?: string;
  detectionType?: string;
}

export class KenobiHelper {
  private readonly logger = new Logger();

  constructor(private kenobiConfig: KenobiConfig) {
    if (!kenobiConfig.kenobiApiUrl && kenobiConfig.nodeEnv !== "test") {
      throw new Error("KenobiConfig must include kenobiApiUrl");
    }
  }

  private async request<T>(
    endpoint: string,
    method: "GET" | "POST" = "GET",
    body?: Record<string, unknown>,
  ): Promise<T> {
    const { kenobiApiUrl, kenobiApiToken } = this.kenobiConfig;
    const url = `${kenobiApiUrl}/${endpoint}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (kenobiApiToken) {
      headers["Authorization"] = `Bearer ${kenobiApiToken}`;
    }

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "Failed to read error body");
        throw new Error(`Kenobi API request failed: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error(`Kenobi API request failed for ${method} ${url}`, error);
      throw error;
    }
  }

  async detectWashTrade(request: WashTradeDetectionRequest): Promise<WashTradeDetectionResponse> {
    this.logger.log(`Detecting wash trade for order ${request.id}`, Logger.types.INFO);

    try {
      const response = await this.request<WashTradeDetectionResponse>("wash-trades/detect", "POST", {
        ...request,
      });

      this.logger.log(`Wash trade detection result: ${JSON.stringify(response)}`, Logger.types.INFO);
      return response;
    } catch (error) {
      // Return default response on error
      return { detected: false };
    }
  }
}
